import { IntlShape } from 'react-intl';

import {
  formatOwnershipStatus,
  formatPaymentMethod,
  formatResidentialStatusFR,
} from '@main/blocks/results/views/InsuranceDetailsView/helpers';
import { Country, Insurance, PaymentMethod } from '@main/types';
import { formatInsuranceSubType } from '@main/utils/format';
import {
  isCarCoverageMtplEE,
  isCarInsuranceEE,
  isCarInsuranceFR,
  isHomeInsuranceFR,
} from '@main/utils/typeAssertions';

type FormatInsuranceSubTypeProps = {
  intl: IntlShape;
  insurance: Insurance;
  country: Country;
};

export const getInsuranceDetailsLabels = ({
  intl,
  insurance,
  country,
}: FormatInsuranceSubTypeProps) => {
  const { insuranceType, insuranceSubType, insuranceName } = insurance;

  let insuranceTypeCopy = formatInsuranceSubType({
    intl,
    insuranceType,
    insuranceSubType,
    insuranceName,
    useFallbackMessage: false,
  });

  /**
   * Special handling for car insurance in the EE region
   * https://linear.app/insurely/issue/DAW-768/citadele-ee-should-we-change-insurancetype-for-mptl-insurances
   */
  if (isCarInsuranceEE(insurance) && isCarCoverageMtplEE(insurance.coverage)) {
    insuranceTypeCopy = intl.formatMessage({
      id: 'data-aggregation.insurance.details.insurance-type.mtpl',
      defaultMessage: 'Insurance policy',
    });
  }

  const { paymentMethod } = insurance;
  const formattedPaymentMethod = formatPaymentMethod(
    intl,
    paymentMethod ? PaymentMethod[paymentMethod] : undefined,
  );

  return {
    insuranceType: insuranceTypeCopy,
    information: intl.formatMessage({
      defaultMessage: 'Information',
      id: 'insurance-details.information.info.label',
    }),
    coverage: intl.formatMessage({
      defaultMessage: 'Coverage',
      id: 'insurance-details.information.coverage.label',
    }),
    monthlyCost: intl.formatMessage({
      defaultMessage: 'Monthly cost',
      id: 'insurance-details.information.monthly-cost.label',
    }),
    annualCost: intl.formatMessage({
      defaultMessage: 'Annual cost',
      id: 'insurance-details.information.annual-cost.label',
    }),
    validUntil: intl.formatMessage({
      defaultMessage: 'Valid until',
      id: 'insurance-details.information.valid-until.label',
    }),
    insuranceName: intl.formatMessage({
      defaultMessage: 'Insurance name',
      id: 'insurance-details.information.insurance-name.label',
    }),
    insuranceHolder: intl.formatMessage({
      defaultMessage: 'Policy holder',
      id: 'insurance-details.information.insurance-holder.label',
    }),
    insuranceCompany: intl.formatMessage({
      defaultMessage: 'Insurance company',
      id: 'insurance-details.information.insurance-company.label',
    }),
    policyNumber: intl.formatMessage({
      defaultMessage: 'Policy number',
      id: 'insurance-details.information.policy-number.label',
    }),
    paymentType: intl.formatMessage({
      defaultMessage: 'Payment methods',
      id: 'insurance-details.information.payment-type.label',
    }),
    paymentMethod: formattedPaymentMethod,
    addons: intl.formatMessage({
      defaultMessage: 'Add-ons',
      id: 'insurance-details.information.add-ons.label',
    }),
    noAddons: intl.formatMessage({
      defaultMessage: 'No add-ons',
      id: 'insurance-details.information.no-addons.label',
    }),
    addOnExplanation: intl.formatMessage({
      defaultMessage: 'Add-ons are optional additions to the insurance policy.',
      id: 'insurance-details.information.add-on-explanation.label',
    }),
    addOn: intl.formatMessage({
      defaultMessage: 'Add-on',
      id: 'insurance-details.information.add-on.label',
    }),
    addOnPurchased: intl.formatMessage({
      defaultMessage: 'Purchased add-on',
      id: 'insurance-details.information.add-on-purchased.label',
    }),
    addOnAvailable: intl.formatMessage({
      defaultMessage: 'Add-on available',
      id: 'insurance-details.information.add-on-available.label',
    }),
    included: intl.formatMessage({
      defaultMessage: 'Included',
      id: 'insurance-details.information.included.label',
    }),
    notIncluded: intl.formatMessage({
      defaultMessage: 'Not included',
      id: 'insurance-details.information.not-included.label',
    }),
    emptyStateTitle: intl.formatMessage(
      {
        defaultMessage: 'Your {insuranceType} is not available right now',
        id: 'insurance-details.information.empty-state-title.label',
      },
      { insuranceType: insuranceTypeCopy?.toLowerCase() ?? 'insurance' },
    ),
    emptyStateText: intl.formatMessage({
      defaultMessage:
        'At this point we are unable to find the coverage included in this insurance.',
      id: 'insurance-details.information.empty-state-text.label',
    }),
    emptyStateAdditionalText: intl.formatMessage({
      defaultMessage:
        'We are always working on improving our services, we hope to have a solution for it as soon as possible.',
      id: 'insurance-details.information.empty-state-additional-text.label',
    }),
    unknown: intl.formatMessage({
      defaultMessage: 'Unknown',
      id: 'insurance-details.information.unknown.label',
    }),
    notAvailable: intl.formatMessage({
      defaultMessage: 'Not available',
      id: 'insurance-details.information.not-available.label',
    }),
    ...(country === Country.fr && {
      homeOwnership: intl.formatMessage({
        defaultMessage: 'Home ownership',
        id: 'insurance-details.information.home-ownership.label',
      }),
      homeOwnershipDetails:
        isHomeInsuranceFR(insurance) && insurance.ownershipStatus
          ? formatOwnershipStatus(intl, insurance.ownershipStatus)
          : intl.formatMessage({
              defaultMessage: 'Unknown',
              id: 'insurance-details.information.unknown.label',
            }),
      residentialStatus: intl.formatMessage({
        defaultMessage: 'Residential status',
        id: 'insurance-details.information.residential-status.label',
      }),
      residentialStatusDetails:
        isHomeInsuranceFR(insurance) && insurance.residentialStatus
          ? formatResidentialStatusFR(intl, insurance.residentialStatus)
          : intl.formatMessage({
              defaultMessage: 'Unknown',
              id: 'insurance-details.information.unknown.label',
            }),
      bonusMalusCoefficient:
        isCarInsuranceFR(insurance) && insurance?.bonusMalus?.coefficient
          ? intl.formatMessage({
              id: 'insurance-details.information.bonus-malus-coefficient.label',
              defaultMessage: 'Reduction increase coefficient',
            })
          : intl.formatMessage({
              defaultMessage: 'Unknown',
              id: 'insurance-details.information.unknown.label',
            }),
    }),
  };
};
